// ============================================================================
// @torre/expenses — Pago de plataformas: modelo de cobro (CC-CONSOLE #956)
// ============================================================================
// Cada plataforma se cobra de una de tres formas:
//
//   · asiento  → asientos × precio por asiento (por periodo de cobro)
//   · servicio → tarifa fija por periodo (lo que ya existía: `expectedAmount`)
//   · consumo  → unidad (tokens, GB, minutos…) + costo estimado mensual; lo
//                real sale de los cargos, mes a mes.
//
// Una plataforma guardada antes de #956 no tiene `billingModel`: se lee como
// `servicio` y nada se migra en la base. Lógica pura, sin I/O.
// ============================================================================

import type { Expense, Platform, PlatformFrequency } from "./index";

export const PLATFORM_BILLING_MODELS = ["asiento", "servicio", "consumo"] as const;
export type PlatformBillingModel = (typeof PLATFORM_BILLING_MODELS)[number];

export const PLATFORM_BILLING_MODEL_LABELS: Record<PlatformBillingModel, { es: string; en: string }> = {
  asiento: { es: "Por asiento", en: "Per seat" },
  servicio: { es: "Por servicio", en: "Flat fee" },
  consumo: { es: "Por consumo", en: "Usage-based" },
};

/** Unidades sugeridas para el modelo `consumo` (se acepta cualquier texto). */
export const CONSUMPTION_UNITS = ["tokens", "GB", "minutos", "solicitudes", "horas", "mensajes", "créditos"] as const;

/** Alza tolerada por defecto del consumo contra el promedio de 3 meses. */
export const DEFAULT_SPIKE_THRESHOLD = 0.25;

const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const isDate = (v: unknown): v is string => {
  if (typeof v !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
};
const finite = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

/** Modelo de cobro de una plataforma; sin él (plataformas previas a #956), `servicio`. */
export function billingModelOf(p: Pick<Platform, "billingModel">): PlatformBillingModel {
  return PLATFORM_BILLING_MODELS.includes(p.billingModel as PlatformBillingModel) ? (p.billingModel as PlatformBillingModel) : "servicio";
}

/** Meses que cubre un periodo de cobro. `uso` no tiene periodo fijo. */
export const FREQUENCY_MONTHS: Record<PlatformFrequency, number | null> = {
  mensual: 1,
  trimestral: 3,
  anual: 12,
  semanal: 12 / 52,
  uso: null,
};

/** Precio unitario: por asiento (asiento) o la tarifa (servicio). Consumo no tiene. */
export function platformUnitPrice(p: Platform): number | null {
  const model = billingModelOf(p);
  if (model === "asiento") return finite(p.seatPrice) ? p.seatPrice : null;
  if (model === "servicio") return finite(p.expectedAmount) ? (p.expectedAmount as number) : null;
  return null;
}

/** Costo de un periodo de cobro en la moneda de la plataforma. */
export function platformPeriodCost(p: Platform): number | null {
  const model = billingModelOf(p);
  if (model === "asiento") {
    if (!finite(p.seats) || !finite(p.seatPrice)) return finite(p.expectedAmount) ? (p.expectedAmount as number) : null;
    return r2(p.seats * p.seatPrice);
  }
  return finite(p.expectedAmount) ? (p.expectedAmount as number) : null;
}

/**
 * Costo mensual equivalente, en la moneda de la plataforma. Las anuales y
 * trimestrales se prorratean; el consumo ya es un estimado mensual. Una
 * plataforma «por uso» con tarifa fija no tiene equivalente mensual (`null`).
 */
export function platformMonthlyCost(p: Platform): number | null {
  const cost = platformPeriodCost(p);
  if (cost == null) return null;
  if (billingModelOf(p) === "consumo") return r2(cost);
  const months = FREQUENCY_MONTHS[p.frequency];
  if (months == null) return p.frequency === "uso" && billingModelOf(p) === "asiento" ? r2(cost) : null;
  return r2(cost / months);
}

/**
 * Deja los campos de cobro consistentes antes de guardar: en `asiento`,
 * `expectedAmount` = asientos × precio (lo que usan el reporte mensual y el
 * resumen); los campos de otro modelo se limpian para no mentir en la tabla.
 */
export function normalizePlatformBilling<T extends Partial<Platform>>(input: T): T {
  const out: T = { ...input };
  const model = billingModelOf(out);
  out.billingModel = model;
  if (model === "asiento") {
    if (finite(out.seats) && finite(out.seatPrice)) out.expectedAmount = r2(out.seats * out.seatPrice);
    out.consumptionUnit = null;
    const holders = Array.isArray(out.seatHolders) ? out.seatHolders : [];
    out.seatHolders = holders
      .map((h) => ({ name: String(h?.name ?? "").trim(), email: h?.email ? String(h.email).trim().toLowerCase() : null }))
      .filter((h) => h.name || h.email);
  } else {
    out.seats = null;
    out.seatPrice = null;
    out.seatHolders = [];
    if (model === "servicio") out.consumptionUnit = null;
  }
  return out;
}

export interface BillingValidation {
  valid: boolean;
  errors: string[];
}

/** Validación de los campos de cobro (#956). Complementa `validatePlatform`. */
export function validatePlatformBilling(input: Partial<Platform>): BillingValidation {
  const errors: string[] = [];
  if (input.billingModel != null && !PLATFORM_BILLING_MODELS.includes(input.billingModel as PlatformBillingModel)) {
    errors.push("billingModel: asiento, servicio o consumo");
  }
  const model = billingModelOf(input);
  if (input.seats != null && (!Number.isInteger(input.seats) || input.seats < 0)) errors.push("seats: entero ≥ 0");
  if (input.seatPrice != null && (!finite(input.seatPrice) || input.seatPrice < 0)) errors.push("seatPrice: número ≥ 0");
  if (model === "asiento") {
    if (input.seats == null) errors.push("seats: requerido en el modelo por asiento");
    if (input.seatPrice == null) errors.push("seatPrice: requerido en el modelo por asiento");
    const holders = Array.isArray(input.seatHolders) ? input.seatHolders : [];
    if (finite(input.seats) && holders.length > input.seats) errors.push("seatHolders: hay más ocupantes que asientos");
  }
  if (input.seatHolders != null && !Array.isArray(input.seatHolders)) errors.push("seatHolders: debe ser una lista");
  if (input.consumptionUnit != null && String(input.consumptionUnit).length > 40) errors.push("consumptionUnit: máximo 40 caracteres");
  if (input.nextRenewal != null && input.nextRenewal !== "" && !isDate(input.nextRenewal)) errors.push("nextRenewal: YYYY-MM-DD");
  if (input.spikeThreshold != null && (!finite(input.spikeThreshold) || input.spikeThreshold <= 0 || input.spikeThreshold > 10)) {
    errors.push("spikeThreshold: mayor que 0 (0.25 = 25 %)");
  }
  return { valid: errors.length === 0, errors };
}

// --- Próxima renovación --------------------------------------------------------
function addMonths(date: string, months: number, day?: number | null): string {
  const [y, m, d] = date.split("-").map(Number);
  const want = day ?? d;
  const last = new Date(Date.UTC(y, m - 1 + months + 1, 0)).getUTCDate();
  return new Date(Date.UTC(y, m - 1 + months, Math.min(want, last))).toISOString().slice(0, 10);
}

function addDays(date: string, days: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
}

/**
 * Próxima fecha de cobro/renovación a partir de hoy (YYYY-MM-DD). Usa
 * `nextRenewal` si está (y lo recorre por periodos si ya pasó); si no, el día
 * de cobro para las mensuales. `null` si no se puede saber.
 */
export function platformNextRenewal(p: Pick<Platform, "frequency" | "billingDay" | "nextRenewal" | "active">, today: string): string | null {
  if (p.active === false || !isDate(today)) return null;
  const months = FREQUENCY_MONTHS[p.frequency];
  if (p.nextRenewal && isDate(p.nextRenewal)) {
    let next = p.nextRenewal;
    if (months == null) return next >= today ? next : null;
    let guard = 0;
    while (next < today && guard++ < 1000) next = p.frequency === "semanal" ? addDays(next, 7) : addMonths(next, months);
    return next;
  }
  if (p.frequency === "mensual" && p.billingDay != null && p.billingDay >= 1 && p.billingDay <= 31) {
    const thisMonth = addMonths(`${today.slice(0, 7)}-01`, 0, p.billingDay);
    return thisMonth >= today ? thisMonth : addMonths(`${today.slice(0, 7)}-01`, 1, p.billingDay);
  }
  return null;
}

// --- Tipo de cambio de referencia ------------------------------------------------
/**
 * Último tipo de cambio conocido por moneda, solo el valor, tomado de los gastos
 * con tipo de cambio (el más reciente gana). Sirve para dar un total aproximado
 * en pesos. (`latestExchangeRates` de index.ts devuelve además la fecha.)
 */
export function latestRateValues(expenses: readonly Pick<Expense, "currency" | "exchangeRate" | "date">[], base = "MXN"): Record<string, number> {
  const best: Record<string, { date: string; rate: number }> = {};
  for (const e of expenses) {
    const cur = String(e.currency || base).toUpperCase();
    if (cur === base || !finite(e.exchangeRate) || (e.exchangeRate as number) <= 0) continue;
    if (!best[cur] || e.date > best[cur].date) best[cur] = { date: e.date, rate: e.exchangeRate as number };
  }
  return Object.fromEntries(Object.entries(best).map(([k, v]) => [k, v.rate]));
}

// --- Encabezado --------------------------------------------------------------------
export interface BillingModelTotals {
  count: number;
  /** Costo mensual equivalente por moneda (no se mezclan monedas). */
  monthlyByCurrency: Record<string, number>;
  seats: number;
}

export interface PlatformBillingOverview {
  activeCount: number;
  seatsTotal: number;
  /** Seats con ocupante registrado (solo modelo asiento). */
  seatsAssigned: number;
  monthlyByCurrency: Record<string, number>;
  /** Total aproximado en moneda base con `rates`; `null` si falta el tipo de cambio de alguna moneda. */
  monthlyBase: number | null;
  /** Monedas sin tipo de cambio de referencia (no entran a `monthlyBase`). */
  missingRates: string[];
  /** Activas sin costo capturado (no suman). */
  withoutCost: number;
  byModel: Record<PlatformBillingModel, BillingModelTotals>;
}

export function platformBillingOverview(platforms: readonly Platform[], rates: Record<string, number> = {}, base = "MXN"): PlatformBillingOverview {
  const byModel = Object.fromEntries(
    PLATFORM_BILLING_MODELS.map((m) => [m, { count: 0, monthlyByCurrency: {}, seats: 0 } as BillingModelTotals]),
  ) as Record<PlatformBillingModel, BillingModelTotals>;
  const out: PlatformBillingOverview = { activeCount: 0, seatsTotal: 0, seatsAssigned: 0, monthlyByCurrency: {}, monthlyBase: 0, missingRates: [], withoutCost: 0, byModel };
  const missing = new Set<string>();
  for (const p of platforms) {
    if (p.active === false) continue;
    const model = billingModelOf(p);
    out.activeCount += 1;
    byModel[model].count += 1;
    if (model === "asiento" && finite(p.seats)) {
      out.seatsTotal += p.seats;
      byModel.asiento.seats += p.seats;
      out.seatsAssigned += Math.min(p.seats, Array.isArray(p.seatHolders) ? p.seatHolders.length : 0);
    }
    const monthly = platformMonthlyCost(p);
    if (monthly == null) {
      out.withoutCost += 1;
      continue;
    }
    const cur = String(p.currency || base).toUpperCase();
    out.monthlyByCurrency[cur] = r2((out.monthlyByCurrency[cur] || 0) + monthly);
    byModel[model].monthlyByCurrency[cur] = r2((byModel[model].monthlyByCurrency[cur] || 0) + monthly);
    const rate = cur === base ? 1 : rates[cur];
    if (finite(rate) && rate > 0) out.monthlyBase = r2((out.monthlyBase as number) + monthly * rate);
    else missing.add(cur);
  }
  out.missingRates = [...missing].sort();
  if (missing.size) out.monthlyBase = null;
  return out;
}

// --- Tabla ordenable y CSV ------------------------------------------------------------
export interface PlatformBillingRow {
  id: string;
  name: string;
  /** Plataforma (herramienta del equipo) o infraestructura (ver platformKind.ts). */
  kind: "plataforma" | "infraestructura";
  vendor: string | null;
  model: PlatformBillingModel;
  seats: number | null;
  unitPrice: number | null;
  /** Unidad de consumo (solo `consumo`). */
  unit: string | null;
  monthlyCost: number | null;
  currency: string;
  frequency: PlatformFrequency;
  nextRenewal: string | null;
  card: string | null;
  owner: string | null;
  active: boolean;
}

export function platformBillingRows(platforms: readonly Platform[], today: string): PlatformBillingRow[] {
  return platforms.map((p) => {
    const model = billingModelOf(p);
    const card = p.cardLast4 ? `•${p.cardLast4}` : p.paymentMethod ? String(p.paymentMethod) : null;
    return {
      id: p.id,
      name: p.name,
      kind: p.category === "infraestructura" ? "infraestructura" : "plataforma",
      vendor: p.vendor || null,
      model,
      seats: model === "asiento" && finite(p.seats) ? p.seats : null,
      unitPrice: platformUnitPrice(p),
      unit: model === "consumo" ? p.consumptionUnit || null : null,
      monthlyCost: platformMonthlyCost(p),
      currency: String(p.currency || "MXN").toUpperCase(),
      frequency: p.frequency,
      nextRenewal: platformNextRenewal(p, today),
      card,
      owner: p.owner || null,
      active: p.active !== false,
    };
  });
}

export const PLATFORM_ROW_SORT_KEYS = ["name", "vendor", "model", "seats", "unitPrice", "monthlyCost", "frequency", "nextRenewal", "card", "owner"] as const;
export type PlatformRowSortKey = (typeof PLATFORM_ROW_SORT_KEYS)[number];

/**
 * Ordena las filas. Los vacíos van siempre al final (en ambos sentidos) y las
 * activas antes que las inactivas. El costo se compara en moneda base cuando
 * hay `rates`; sin tipo de cambio, por el número tal cual.
 */
export function sortPlatformRows(
  rows: readonly PlatformBillingRow[],
  key: PlatformRowSortKey,
  dir: "asc" | "desc" = "asc",
  rates: Record<string, number> = {},
): PlatformBillingRow[] {
  const value = (r: PlatformBillingRow): string | number | null => {
    const v = r[key];
    if (v == null || v === "") return null;
    if ((key === "monthlyCost" || key === "unitPrice") && typeof v === "number") {
      const rate = r.currency === "MXN" ? 1 : rates[r.currency];
      return finite(rate) ? v * rate : v;
    }
    return typeof v === "number" ? v : String(v).toLowerCase();
  };
  const sign = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (a.active !== b.active) return a.active ? -1 : 1;
    const va = value(a);
    const vb = value(b);
    if (va == null && vb == null) return a.name.localeCompare(b.name);
    if (va == null) return 1;
    if (vb == null) return -1;
    const cmp = typeof va === "number" && typeof vb === "number" ? va - vb : String(va).localeCompare(String(vb), "es");
    return cmp === 0 ? a.name.localeCompare(b.name) : cmp * sign;
  });
}

const csvCell = (v: unknown): string => {
  const s = v == null ? "" : String(v);
  return /[",\n\r;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** CSV de la tabla de Pago de plataformas (encabezados en el idioma pedido). */
export function platformRowsToCsv(rows: readonly PlatformBillingRow[], language: "es" | "en" = "es"): string {
  const es = language !== "en";
  const header = es
    ? ["Plataforma", "Tipo", "Proveedor", "Modelo de cobro", "Asientos", "Precio unitario", "Unidad de consumo", "Costo mensual equivalente", "Moneda", "Frecuencia", "Próxima renovación", "Tarjeta/cuenta", "Responsable", "Activa"]
    : ["Platform", "Type", "Vendor", "Billing model", "Seats", "Unit price", "Usage unit", "Monthly equivalent cost", "Currency", "Frequency", "Next renewal", "Card/account", "Owner", "Active"];
  const lines = [header.map(csvCell).join(",")];
  for (const r of rows) {
    lines.push(
      [
        r.name,
        r.kind === "infraestructura" ? (es ? "Infraestructura" : "Infrastructure") : es ? "Plataforma" : "Platform",
        r.vendor,
        PLATFORM_BILLING_MODEL_LABELS[r.model][es ? "es" : "en"],
        r.seats,
        r.unitPrice,
        r.unit,
        r.monthlyCost,
        r.currency,
        r.frequency,
        r.nextRenewal,
        r.card,
        r.owner,
        r.active ? (es ? "sí" : "yes") : "no",
      ]
        .map(csvCell)
        .join(","),
    );
  }
  return lines.join("\n");
}

// --- Consumo: histórico real y alzas ------------------------------------------------
export interface ConsumptionMonth {
  period: string;
  /** Pagado en el mes (moneda base, de los cargos). */
  paid: number;
  /** Promedio de los 3 meses anteriores (`null` si no hay 3 meses de historia). */
  average3: number | null;
  /** Variación contra `average3` (0.3 = +30 %). */
  change: number | null;
  spike: boolean;
}

export interface ConsumptionReport {
  platformId: string;
  name: string;
  unit: string | null;
  threshold: number;
  months: ConsumptionMonth[];
  /** El último mes con alza (o `null`). */
  latestSpike: ConsumptionMonth | null;
}

/**
 * Histórico real mes a mes de una plataforma de consumo y aviso de alza: un
 * mes está en alza si supera el promedio de los 3 meses anteriores por más
 * del umbral. Sin 3 meses previos, o con promedio 0, no se avisa (no hay base).
 */
export function consumptionHistory(months: readonly { period: string; paid: number }[], threshold = DEFAULT_SPIKE_THRESHOLD): ConsumptionMonth[] {
  const sorted = [...months].sort((a, b) => a.period.localeCompare(b.period));
  return sorted.map((m, i) => {
    if (i < 3) return { period: m.period, paid: r2(m.paid), average3: null, change: null, spike: false };
    const prev = sorted.slice(i - 3, i);
    const average3 = r2(prev.reduce((s, x) => s + x.paid, 0) / 3);
    if (average3 <= 0) return { period: m.period, paid: r2(m.paid), average3, change: null, spike: false };
    const change = Math.round(((m.paid - average3) / average3) * 1000) / 1000;
    return { period: m.period, paid: r2(m.paid), average3, change, spike: change > threshold };
  });
}

/** Reporte de consumo de las plataformas `consumo` a partir del pagado mes a mes. */
export function consumptionReports(
  platforms: readonly Platform[],
  paidByPlatform: readonly { platformId: string; months: readonly { period: string; paid: number }[] }[],
): ConsumptionReport[] {
  const out: ConsumptionReport[] = [];
  for (const p of platforms) {
    if (billingModelOf(p) !== "consumo") continue;
    const paid = paidByPlatform.find((x) => x.platformId === p.id);
    const threshold = finite(p.spikeThreshold) && (p.spikeThreshold as number) > 0 ? (p.spikeThreshold as number) : DEFAULT_SPIKE_THRESHOLD;
    const months = consumptionHistory(paid ? paid.months : [], threshold);
    const spikes = months.filter((m) => m.spike);
    out.push({ platformId: p.id, name: p.name, unit: p.consumptionUnit || null, threshold, months, latestSpike: spikes.length ? spikes[spikes.length - 1] : null });
  }
  return out;
}
